import React, { FC, useEffect, useState } from "react";
import { Box, Button, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { convertEndTimeToSecsTillExpiry } from "@/js/utils/timeUtils";
import StandardModal from "@/js/components/shared/StandardModal";
import iconClock from "@/assets/IconClock.svg?url";
import { colours } from "@/utils/themeUtils";
import { ClassroomSessionTimeSelector } from "../ClassroomSessionTimeSelector";
import { withRouter } from "../Root/withRouter";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { oneMinuteFromNow, padTimes } from "../ClassroomSessionTimeSelectorHelpers";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { Notice } from "@/components/Notice";

dayjs.extend(customParseFormat);

interface Props {
	open: boolean;
	onExtendError: boolean;
	durationPlaceholder: string;
	inClassroom: boolean;
	router: IComponentRouter;
}

const ClassroomEditSession: FC<Props> = (props) => {
	const classroomId = useSelectedClassroomStore((state) => state.id);

	const [timeValue, setTimeValue] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	// This error comes from timer and decides whether save button should be disabled
	const [hasTimeError, setHasTimeError] = useState<string | null>(null);
	const [requestOngoing, setRequestOngoing] = useState<boolean | null>(null);
	const [isMonitoringClass, setIsMonitoringClass] = useState(false);

	const onChangeClassroomConfigStore = (state: { selectedClassroomMonitoring: boolean }) => {
		setIsMonitoringClass(state.selectedClassroomMonitoring);
	};

	useEffect(() => {
		const unsubFromClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomConfigStore(newState);
			}
		);
		return () => {
			unsubFromClassroomSessionStore();
		};
	}, []);

	useEffect(() => {
		if (!props.open) {
			setTimeValue(null);
			setError(null);
			setRequestOngoing(null);
		}
		// Only set error text if the form is already open (ie if we're waiting for an API response)
		if (props.open && props.onExtendError) {
			setError("Cannot extend the class due to time restrictions set by your school");
			setRequestOngoing(false);
		}
	}, [props.open, props.onExtendError]);

	const timeSelectedHandler = (value: number | null, hasTimeError: string | null) => {
		setTimeValue(value);
		setError(null);
		setHasTimeError(hasTimeError);
	};

	const handleExtendSession = () => {
		if (!timeValue) return;
		// Ensure the end time is in the future
		const timeObject = dayjs(padTimes(timeValue), "HHmm", true);
		if (timeObject.isBefore(oneMinuteFromNow())) {
			setError("End time cannot be in the past");
			return;
		}

		if (timeValue && !requestOngoing) {
			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);

			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: convertEndTimeToSecsTillExpiry(timeValue),
				router: props.router,
				monitoringClass: isMonitoringClass,
				inClassroom: props.inClassroom,
			});
		} else if (!props.onExtendError) {
			setError("Please select a duration");
		}
	};

	const handleClose = () => {
		useClassroomSessionStore.getState().handleShowEditClassModal({ show: false });
		useClassroomSessionStore.getState().handleSetClassSessionError({ error: false });
		setTimeValue(null);
		setError(null);
		setRequestOngoing(false);
	};

	const renderActions = () => (
		<Button onClick={handleExtendSession} isLoading={requestOngoing ?? false} isDisabled={error !== null || hasTimeError !== null}>
			Save
		</Button>
	);

	return (
		<StandardModal
			size="medium"
			title={isMonitoringClass ? "Edit Monitoring Session" : "Edit Class Session"}
			actions={!props.durationPlaceholder ? null : renderActions()}
			open={props.open}
			close={handleClose}
		>
			<form onSubmit={(ev) => ev.preventDefault()}>
				{props.durationPlaceholder ? (
					<>
						<Notice
							status="info"
							children={
								<>
									Please enter time in <b>hh:mm</b> format
								</>
							}
						/>
						<Flex alignItems="top" gap={3} py={1} mt={4}>
							<Flex alignItems="center" gap={2} py={1} my={0} height={"38px"}>
								<Image src={iconClock} alt=""></Image>
								<Text>In session until</Text>
							</Flex>
							<Box>
								<ClassroomSessionTimeSelector preselected={props.durationPlaceholder} onChange={timeSelectedHandler} />
								{error && (
									<Text position="absolute" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
										{error}
									</Text>
								)}
							</Box>
						</Flex>
					</>
				) : (
					<Flex justifyContent="center" alignItems="center" height="3em">
						<Spinner size="md" />
					</Flex>
				)}
			</form>
		</StandardModal>
	);
};

export default withRouter(ClassroomEditSession);
